import {
  OptionLegPrefill,
  OptionOrderTicketPrefill,
  OrderSide,
  OrderTicketPrefill,
  OrderType,
  StockOrderTicketPrefill,
} from '../../../types/trading';

interface ParsedOptionSymbol {
  underlying: string;
  expiration: string;
  optionSide: 'call' | 'put';
  strike: number;
}

const parseOptionSymbol = (symbol: string): ParsedOptionSymbol | null => {
  const normalized = symbol.trim().toUpperCase();
  const match = normalized.match(/^O:([A-Z]+)(\d{6})([CP])(\d+)$/);
  if (!match) return null;

  const [, ticker, yymmdd, cpFlag, strikeCode] = match;
  const yy = yymmdd.slice(0, 2);
  const mm = yymmdd.slice(2, 4);
  const dd = yymmdd.slice(4, 6);

  return {
    underlying: ticker,
    expiration: `20${yy}-${mm}-${dd}`,
    optionSide: cpFlag === 'C' ? 'call' : 'put',
    strike: parseInt(strikeCode, 10) / 1000,
  };
};

const normalizeOrderType = (value: unknown): OrderType | null => {
  if (value === 'market' || value === 'limit' || value === 'best_fill') return value;
  return null;
};

const normalizeOptionOrderType = (value: unknown): Extract<OrderType, 'market' | 'best_fill'> | null => {
  if (value === 'market' || value === 'best_fill') return value;
  return null;
};

const normalizeOrderSide = (value: unknown): OrderSide | null => {
  if (value === 'buy' || value === 'sell') return value;
  return null;
};

const normalizePositiveInteger = (value: unknown): number | null => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  const rounded = Math.round(value);
  return rounded > 0 ? rounded : null;
};

const normalizePositiveNumber = (value: unknown): number | null => {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) return null;
  return value;
};

const normalizeTicker = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const normalized = value.trim().toUpperCase();
  return normalized ? normalized : null;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const parseOptionLeg = (
  raw: unknown,
  defaultUnderlying: string | null,
): (OptionLegPrefill & { underlying: string }) | null => {
  if (!isRecord(raw)) return null;

  const legSide = normalizeOrderSide(raw.side);
  const legQuantity = normalizePositiveInteger(raw.quantity);
  const legOptionSide = raw.option_side === 'call' || raw.option_side === 'put' ? raw.option_side : null;
  const legExpiration = typeof raw.expiration === 'string' ? raw.expiration.trim() : '';
  const legStrike = normalizePositiveNumber(raw.strike);
  const legSymbol = typeof raw.symbol === 'string' && raw.symbol.trim() ? raw.symbol.trim().toUpperCase() : null;

  const parsedFromSymbol = legSymbol ? parseOptionSymbol(legSymbol) : null;
  const resolvedUnderlying = defaultUnderlying
    ?? normalizeTicker(raw.underlying)
    ?? normalizeTicker(raw.option_underlying)
    ?? parsedFromSymbol?.underlying
    ?? 'AAPL';

  const optionSide = legOptionSide ?? parsedFromSymbol?.optionSide ?? 'call';
  const expiration = legExpiration || parsedFromSymbol?.expiration || '';
  const strike = legStrike ?? parsedFromSymbol?.strike ?? 100;

  const leg: OptionLegPrefill & { underlying: string } = {
    side: legSide ?? 'buy',
    option_side: optionSide,
    expiration,
    strike,
    quantity: legQuantity ?? 1,
    underlying: resolvedUnderlying,
  };

  if (legSymbol) leg.symbol = legSymbol;
  return leg;
};

export const buildManualOrderPrefillFromDraft = (payload: unknown): OrderTicketPrefill | null => {
  if (!isRecord(payload)) return null;

  const instrumentType = payload.instrument_type;
  const explicitUnderlying = normalizeTicker(payload.underlying);
  const rawLegs = Array.isArray(payload.option_legs) ? payload.option_legs : [];
  const parsedLegs = rawLegs
    .map((leg) => parseOptionLeg(leg, explicitUnderlying))
    .filter((leg): leg is OptionLegPrefill & { underlying: string } => Boolean(leg));

  const maybeCanonicalSymbol = typeof payload.symbol === 'string' ? payload.symbol : null;
  const parsedFromRootSymbol = maybeCanonicalSymbol ? parseOptionSymbol(maybeCanonicalSymbol) : null;

  const optionUnderlying = explicitUnderlying ?? parsedFromRootSymbol?.underlying ?? null;
  const optionOrderType = normalizeOptionOrderType(payload.order_type);

  const shouldParseOption = instrumentType === 'option' || parsedLegs.length > 0 || Boolean(parsedFromRootSymbol);
  if (shouldParseOption) {
    const fallbackUnderlying = optionUnderlying ?? 'AAPL';
    const optionLegs = parsedLegs.length > 0
      ? parsedLegs
      : [{
          side: normalizeOrderSide(payload.side) ?? 'buy',
          option_side:
            (payload.option_side === 'call' || payload.option_side === 'put' ? payload.option_side : null)
            ?? parsedFromRootSymbol?.optionSide
            ?? 'call',
          expiration:
            (typeof payload.option_expiration === 'string' ? payload.option_expiration.trim() : '')
            || parsedFromRootSymbol?.expiration
            || '',
          strike:
            normalizePositiveNumber(payload.option_strike)
            ?? parsedFromRootSymbol?.strike
            ?? 100,
          quantity: normalizePositiveInteger(payload.quantity) ?? 1,
          symbol: maybeCanonicalSymbol?.trim().toUpperCase() || undefined,
          underlying: fallbackUnderlying,
        }];

    const resolvedUnderlying = optionUnderlying ?? optionLegs[0].underlying;
    if (optionLegs.some((leg) => leg.underlying !== resolvedUnderlying)) return null;

    const optionPrefill: OptionOrderTicketPrefill = {
      instrument_type: 'option',
      underlying: resolvedUnderlying,
      option_legs: optionLegs.map(({ underlying, ...leg }) => leg),
    };

    if (optionOrderType) {
      optionPrefill.order_type = optionOrderType;
    }
    return optionPrefill;
  }

  const symbol = normalizeTicker(payload.symbol);
  if (!symbol) return null;

  const side = normalizeOrderSide(payload.side) ?? 'buy';
  const quantity = normalizePositiveInteger(payload.quantity) ?? 1;

  const stockPrefill: StockOrderTicketPrefill = {
    symbol,
    side,
    quantity,
  };

  const orderType = normalizeOrderType(payload.order_type);
  if (orderType) stockPrefill.order_type = orderType;

  const limitPrice = normalizePositiveNumber(payload.limit_price);
  if (limitPrice != null) stockPrefill.limit_price = limitPrice;

  return stockPrefill;
};
